import { ALL_UPGRADES, getUpgradeById } from './UpgradeData';
import { ALL_WEAPONS, getWeaponById } from './WeaponData';
import { type ScoredItem, priceMultFromScore, rollScore } from './Quality';
import { generateItemName } from './ItemNames';

// ─── Pricing ─────────────────────────────────────────────

export function isWeaponItem(item: ScoredItem): boolean {
  return item.id.startsWith('wpn_');
}

export function getItemTier(item: ScoredItem): number {
  if (isWeaponItem(item)) {
    return getWeaponById(item.id)?.tier ?? 1;
  }
  return getUpgradeById(item.id)?.tier ?? 1;
}

export function getItemBuyPrice(item: ScoredItem): number {
  const tier = getItemTier(item);
  const baseCost = isWeaponItem(item) ? tier * tier * 75 : tier * tier * 50;
  return Math.ceil(baseCost * priceMultFromScore(item.score));
}

export function getItemSellPrice(item: ScoredItem, sellBonus: number = 0): number {
  return Math.floor(getItemBuyPrice(item) * (0.4 + sellBonus));
}

// ─── Tier unlocks (exploration % thresholds) ────────────
// Non-linear: early tiers unlock quickly, top tiers need serious exploration.

/** Exploration % required for each upgrade tier (index 0 = tier 1, etc.) */
const UPGRADE_TIER_THRESHOLDS = [0, 5, 12, 20, 30, 42, 55, 68, 82, 95];
/** Exploration % required for each weapon tier */
const WEAPON_TIER_THRESHOLDS  = [0, 10, 25, 45, 70];

function maxTierForPct(pct: number, thresholds: number[]): number {
  let tier = 1;
  for (let i = 1; i < thresholds.length; i++) {
    if (pct >= thresholds[i]!) tier = i + 1;
    else break;
  }
  return tier;
}

// ─── Trader System ───────────────────────────────────────

const REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes
const STOCK_SIZE = 20;
const UPGRADE_COUNT = 14;

/**
 * Trader stock is rolled with a distance proxy tied to exploration %,
 * so deeper runs unlock stronger merchant rolls alongside deeper drops.
 */
function stockScoreDist(explorationPct: number): number {
  // 0 % → ~20 (score ceiling ≈ 47), 100 % → ~170 (score ceiling = 100)
  return 20 + explorationPct * 1.5;
}

export class TraderSystem {
  private _stock: (ScoredItem | null)[] = [];
  private _generatedAt = 0;

  constructor() {
    this.generateStock(0);
  }

  get stock(): readonly (ScoredItem | null)[] { return this._stock; }
  get generatedAt(): number { return this._generatedAt; }

  generateStock(explorationPct: number) {
    const maxUpgTier = maxTierForPct(explorationPct, UPGRADE_TIER_THRESHOLDS);
    const maxWpnTier = maxTierForPct(explorationPct, WEAPON_TIER_THRESHOLDS);

    const availableUpgrades = ALL_UPGRADES.filter(u => u.tier <= maxUpgTier);
    const availableWeapons  = ALL_WEAPONS.filter(w => w.tier <= maxWpnTier);
    const scoreDist = stockScoreDist(explorationPct);

    this._stock = [];
    if (availableUpgrades.length > 0) {
      for (let i = 0; i < UPGRADE_COUNT; i++) {
        const def = availableUpgrades[Math.floor(Math.random() * availableUpgrades.length)]!;
        const score = rollScore(scoreDist);
        const name = generateItemName('upgrade', def.category, score);
        this._stock.push({ id: def.id, score, name });
      }
    }
    if (availableWeapons.length > 0) {
      for (let i = 0; i < STOCK_SIZE - UPGRADE_COUNT; i++) {
        const def = availableWeapons[Math.floor(Math.random() * availableWeapons.length)]!;
        const score = rollScore(scoreDist);
        const name = generateItemName('weapon', def.category, score);
        this._stock.push({ id: def.id, score, name });
      }
    }
    this._generatedAt = Date.now();
  }

  refreshIfNeeded(explorationPct: number = 0): boolean {
    if (Date.now() - this._generatedAt >= REFRESH_INTERVAL) {
      this.generateStock(explorationPct);
      return true;
    }
    return false;
  }

  removeFromStock(index: number): ScoredItem | null {
    if (index < 0 || index >= this._stock.length) return null;
    const item = this._stock[index]!;
    this._stock[index] = null;
    return item;
  }

  timeUntilRefresh(): number {
    return Math.max(0, REFRESH_INTERVAL - (Date.now() - this._generatedAt));
  }

  serialize() {
    return {
      stock: [...this._stock],
      generatedAt: this._generatedAt,
    };
  }

  deserialize(data: { stock: (ScoredItem | null)[]; generatedAt: number }) {
    this._stock = data.stock ?? [];
    this._generatedAt = data.generatedAt ?? 0;
  }
}
